import React, { useEffect } from 'react';
import Ring from '@/components/ui/Ring';
import PageTitle from '@/components/ui/PageTitle';
import CompanyLogo from '@/components/ui/CompanyLogo';
import { Button, Input, Col, DatePicker, Row, Radio, Form } from 'antd';
import IconButton from '@/components/ui/IconButton';
import { UploadOutlined } from "@ant-design/icons";
import { DateSelectionType, CapitalizationActualsPlaceholderName, CustomBuiltIndexPlaceholderName } from '@/types/newLinkBuilder';
import { DialogMessageType } from '@/types/commands';
import dayjs from 'dayjs';
import { useCustomBuiltIndexStore } from '@/state/customBuiltIndexStore';
import { buildFormula, FormulaType } from '@/utils/formulaBuilder';
import { sendTemplateToExtension } from '@/helpers/excelExtensionService';

const CustomBuiltIndex: React.FC = () => {
  const {
    dateValue,
    setDateSelectionType,
    setDate,
    setDateCellReference,
    inputRange,
    setInputRange,
    formula,
    setFormula,
    formulaLocation,
    setFormulaLocation,
  } = useCustomBuiltIndexStore((state) => state);

  const handleDateSelectionChange = (selectionType: DateSelectionType) => {
    setDateSelectionType(selectionType);
  };

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    setDate(value ? dayjs(value).toISOString() : '');
  };

  const handleCellReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateCellReference(e.target.value);
  };

  const handlePlaceholder = (name: CapitalizationActualsPlaceholderName | CustomBuiltIndexPlaceholderName) => {
    localStorage.setItem("placeholder", JSON.stringify({
      placeholderName: name,
      data: { formulaLocation },
    }));
    localStorage.setItem("redirectTo", "custom-built-index");
    Office.context.ui.messageParent(JSON.stringify({ action: DialogMessageType.OPEN_PLACEHOLDER }));
  };

  // Build formula whenever inputRange or dateValue changes
  useEffect(() => {
    const buildFormulaAsync = async () => {
      const generatedFormula = await buildFormula(FormulaType.CustomBuiltIndex, {
        inputRange,
        date: dateValue,
      });
      setFormula(generatedFormula);
    };

    buildFormulaAsync();
  }, [inputRange, dateValue.selected, dateValue.date, dateValue.dateCellReference, setFormula]);

  useEffect(() => {
    const placeholder = localStorage.getItem("placeholder");
    const cellAddress = localStorage.getItem("cellAddress");
    const activeCell = localStorage.getItem("activeCell");

    if (activeCell) {
      setFormulaLocation(activeCell);
    }

    if (placeholder && cellAddress) {
      const placeholderData = JSON.parse(placeholder);

      if (placeholderData.data) {
        if (placeholderData.data.formulaLocation !== undefined) {
          setFormulaLocation(placeholderData.data.formulaLocation);
        }
      }

      if (placeholderData.placeholderName === CustomBuiltIndexPlaceholderName.FormulaLocationCellReference) {
        setFormulaLocation(cellAddress);
      }

      if (placeholderData.placeholderName === CustomBuiltIndexPlaceholderName.DateCellReference) {
        setDateSelectionType(DateSelectionType.CellReference);
        setDateCellReference(cellAddress);
      }

      if (placeholderData.placeholderName === CustomBuiltIndexPlaceholderName.InputRangeCellReference) {
        setInputRange(cellAddress);
      }
    }

    return () => {
      localStorage.removeItem("placeholder");
      localStorage.removeItem("cellAddress");
    };
  }, [setFormulaLocation, setDateCellReference, setDateSelectionType, setInputRange]);

  const handleCancel = () => {
    localStorage.removeItem("customBuiltIndexFormData");
  };

  const handleOk = () => {
    localStorage.removeItem("customBuiltIndexFormData");
    localStorage.removeItem("placeholder");
    localStorage.removeItem("cellAddress");
    localStorage.removeItem("activeCell");
    // Send formula and location to parent via messageParent
    Office.context.ui.messageParent(JSON.stringify({
      action: DialogMessageType.ADD_CUSTOM_BUILT_INDEX_FORMULA_TO_CELL,
      data: { formula, address: formulaLocation },
    }));
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <Ring className="mt-2 px-6 py-2 flex flex-col items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-between gap-4 w-full">
          <div className="flex items-center justify-between gap-2 mb-4">
            <PageTitle className="!mb-0 flex items-center gap-2 !text-2xl !font-bold">Create New Custom-Built Index</PageTitle>
            <CompanyLogo domain="morganstanley.com" height={20} />
          </div>

          {/* Add a new step to the instructions */}
          <div className="">
            <p className="text-gray-900 font-bold mb-4">
              We can build a custom index for you and return an Alpha Formula with
              its daily closing share price returned. Follow these steps:
            </p>

            <ol className="list-decimal ml-6 space-y-2 text-gray-800">
              <li>
                <span className="font-bold">Create Excel Input Range </span>
                 and complete the input decisions
              </li>
              <li>
                <span className="font-bold">Return here </span>
                 to Create Alpha Formula and select Input Range
              </li>
              <li>
                Decide on closing share price <span className="font-bold"> date </span>
                 to return with the Alpha Formula and create the formula in your Excel
              </li>
              <li>
                The Alpha Formula for the daily closing index value can be audited with
                the <span className="font-bold"> Alpha Audit</span> button
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <PageTitle className="!mb-0 flex gap-2 !text-2xl !font-bold">Input Range</PageTitle>
            <p>If you <span className="font-bold underline"> do not</span> already have the Custom-Built Index Input Range completed in your Excel, please click here to add the new tab in to your excel and folow the instructions to complete the inputs:</p>
            <Button 
              className="w-fit self-start !border-2 !border-black" 
              type="primary" 
              onClick={async () => {
                await sendTemplateToExtension();
                
                Office.context.ui.messageParent(JSON.stringify({ action: DialogMessageType.CLOSE }));

              }}
            >
              Add Input Range Tab
            </Button>
            <p>Otherwise, <span className='font-bold underline'>select the input range </span> here:</p>  
            <div className="flex items-center gap-2 w-2/5" >
              <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                <Input
                  placeholder="Input Range"
                  value={inputRange}
                  onChange={(e) => setInputRange(e.target.value)}
                />
              </Col>
              <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                <IconButton
                  icon={<UploadOutlined />}
                  onClick={() => handlePlaceholder(CustomBuiltIndexPlaceholderName.InputRangeCellReference)}
                />
              </Col>
            </div>       
          </div>

          <div className="flex flex-col gap-4 w-full">
            <PageTitle className="!mb-0 flex gap-2 !text-2xl !font-bold">Formula Builder</PageTitle>
                <div className="flex flex-col gap-4 w-full">
                  <p className='font-bold'>As of Date:</p>
                  <Radio.Group 
                    value={dateValue.selected}
                    onChange={(e) => handleDateSelectionChange(e.target.value)}
                    className="w-1/2 pl-5"
                    style={{
                      paddingLeft: '20px',
                    }}
                  >
                    <Row
                      align={"middle"}
                      className="flex gap-2"
                    >
                      <Col span={10} style={{ display: 'flex', justifyContent: 'start' }}>
                        <Radio 
                          value={DateSelectionType.Date}
                          className={` ${dateValue.selected === DateSelectionType.Date ? '!text-as-orange-primary !font-bold' : ''}`}
                        >
                          Date Selection:
                        </Radio>
                      </Col>

                      <Col span={10} style={{ display: 'flex', justifyContent: 'start' }}>
                        <DatePicker 
                          format="MM/DD/YYYY" 
                          value={dateValue.date ? dayjs(dateValue.date) : null}
                          onChange={handleDateChange}
                          disabled={dateValue.selected === DateSelectionType.CellReference}
                          disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                          style={{ width: '100%' }}
                        />
                      </Col>
                    </Row>
                    <Row
                        align={"middle"}
                        className="flex gap-2 mt-2"
                    >
                      <Col span={10} style={{ display: 'flex', justifyContent: 'start' }}>
                        <Radio 
                          value={DateSelectionType.CellReference}
                          className={` ${dateValue.selected === DateSelectionType.CellReference ? '!text-as-orange-primary !font-bold' : ''}`}
                        >
                          Cell Reference:
                        </Radio>
                      </Col>

                      <Col span={10} style={{ display: 'flex', justifyContent: 'start' }}>
                          <Input
                              placeholder="A1"
                              id="dateCellReference"
                              value={dateValue.dateCellReference}
                              onChange={handleCellReferenceChange}
                              disabled={dateValue.selected === DateSelectionType.Date}
                          />
                      </Col>

                      <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                          <IconButton
                              icon={<UploadOutlined />}
                              onClick={() => handlePlaceholder(CustomBuiltIndexPlaceholderName.DateCellReference)}
                          />
                      </Col>
                    </Row>
                  </Radio.Group>
                </div>      
          </div>
          <div>
            <Form.Item
              name={"formulaLocation"}
              className="flex flex-col gap-4 !mb-2 w-[calc(50%-8px)]"
            >
              <Row
                align={"middle"}
                className="flex gap-1"
              >
                <Col span={11} style={{ display: 'flex', justifyContent: 'start' }}>
                  <label htmlFor="formulaLocation" className="!font-bold">
                    Formula Location:
                  </label>
                </Col>

                <Col span={10} style={{ display: 'flex', justifyContent: 'start' }}>
                  <Input
                    placeholder="A1"
                    id="formulaLocation"
                    value={formulaLocation}
                    onChange={(e) => setFormulaLocation(e.target.value)}
                    style={{ width: 'calc(100% - 4px)' }}
                  />
                </Col>

                <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                  <IconButton
                    icon={<UploadOutlined />}
                    onClick={() => handlePlaceholder(CustomBuiltIndexPlaceholderName.FormulaLocationCellReference)}
                  />
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              name={"formulaBuilder"}
              style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  textAlign: 'left',
                  gap: '20px',
                  marginBlock: '20px',
              }}
            >
              <span className="!font-bold">
                Formula Builder:
              </span>

              <code
                className="block !p-5 bg-white border border-gray-200 rounded-md font-mono text-sm whitespace-pre-wrap mt-2 min-h-[50px]"
              >
                {formula}
              </code>
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end items-center gap-2.5 my-5 w-full">
          <Button type="primary" onClick={handleOk}>Ok</Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      </Ring>
    </div>
  );
};

export default CustomBuiltIndex;

